import { useCallback, useEffect, useState } from 'react';
import {
  fetchFundingMatches,
  fetchFundingMatchesList,
  runFundingMatches,
} from '../../services/funding/fundingMatchService';

export function useFundingMatch({ clientId, agencyId, filters = {}, autoLoad = true } = {}) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!clientId || !agencyId) {
      setMatches([]);
      return [];
    }

    setLoading(true);
    setError(null);
    try {
      const result = await fetchFundingMatches(clientId, agencyId, filters);
      setMatches(result.matches);
      return result.matches;
    } catch (err) {
      setError(err?.message || 'Failed to load matches');
      setMatches([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [clientId, agencyId, filters.fitTier, filters.lenderType, filters.productType]);

  const run = useCallback(async () => {
    if (!clientId || !agencyId) return [];

    setRunning(true);
    setError(null);
    try {
      const result = await runFundingMatches(clientId, agencyId);
      setMatches(result.matches);
      return result.matches;
    } catch (err) {
      setError(err?.message || 'Failed to run lender matching');
      throw err;
    } finally {
      setRunning(false);
    }
  }, [clientId, agencyId]);

  useEffect(() => {
    if (autoLoad) {
      load();
    }
  }, [autoLoad, load]);

  return { matches, loading, running, error, load, run, setMatches };
}

export function useFundingMatchList({ agencyId, limit = 50 } = {}) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFundingMatchesList({ agencyId, limit });
      setMatches(result.matches);
      return result.matches;
    } catch (err) {
      setError(err?.message || 'Failed to load recent matches');
      setMatches([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [agencyId, limit]);

  useEffect(() => {
    load();
  }, [load]);

  return { matches, loading, error, reload: load };
}
